import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { ChangePasswordPage, LoginPage, RequireAuth, useMe } from "./auth/session";
import { NotFound } from "./pages/Placeholder";
import DashboardPage from "./pages/dashboard/DashboardPage";
import ExtractionPage from "./pages/ingest/ExtractionPage";
import IngestPage, { BatchPage } from "./pages/ingest/IngestPage";
import InspectionsPage from "./pages/inspections/InspectionsPage";
import CrewPage from "./pages/crew/CrewPage";
import CrewSitePage from "./pages/crew/CrewSitePage";
import RoutingPage from "./pages/routing/RoutingPage";
import SchedulePage from "./pages/routing/SchedulePage";
import PlansPage from "./pages/routing/PlansPage";
import DaySheetsPage from "./pages/routing/DaySheetsPage";
import OverridesPage from "./pages/routing/OverridesPage";
import ReportPage from "./pages/routing/ReportPage";
import VisitsDashboard from "./pages/routing/dashboard/VisitsDashboard";
import RedListReportPage from "./pages/reports/RedListReportPage";
import ProvidersPage from "./pages/admin/ProvidersPage";
import UsersPage from "./pages/admin/UsersPage";
import SparesPage from "./pages/admin/SparesPage";
import {
  AuditPage,
  BackloadPage,
  FlangeTablesPage,
  LabourAllowancesPage,
  MeetingPointsPage,
  PriceListsPage,
  SettingsPage,
} from "./pages/admin/ReferencePages";
import HelpPage from "./pages/help/HelpPage";
import RedListPage from "./pages/redlist/RedListPage";
import ExceptionsPage from "./pages/sites/ExceptionsPage";
import SitesPage from "./pages/sites/SitesPage";
import WorkflowPage from "./pages/workflow/WorkflowPage";
import CommercialPage from "./pages/commercial/CommercialPage";
import DocumentPage from "./pages/commercial/DocumentPage";
import TrackerPage from "./pages/tracker/TrackerPage";
import Shell from "./shell/Shell";

/** The report and the dashboard moved from Routing to Reports; a link sent
 * before then still opens, with its period and filters. */
function KeepSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

/** `/quotations/:id` is the same job as `/commercial/:id`. */
function JobRoute() {
  const { id } = useParams();
  return <Navigate to={`/commercial/${id}`} replace />;
}

function BatchRoute() {
  const { id } = useParams();
  return <BatchPage id={Number(id)} />;
}

function Home() {
  const me = useMe();
  return <Navigate to={me.data?.landing ?? "/login"} replace />;
}

function Signed({ children }: { children: React.ReactNode }) {
  const me = useMe();
  if (!me.data) {
    return null;
  }
  return <Shell me={me.data}>{children}</Shell>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <Signed>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/ingest" element={<IngestPage />} />
                <Route path="/ingest/:id" element={<BatchRoute />} />
                <Route path="/sites/redlist" element={<RedListPage />} />
                <Route path="/sites" element={<SitesPage />} />
                <Route path="/sites/:id" element={<SitesPage />} />
                <Route path="/exceptions" element={<ExceptionsPage />} />
                <Route path="/extraction" element={<ExtractionPage />} />
                <Route path="/extraction/:id" element={<ExtractionPage />} />
                <Route path="/workflow" element={<WorkflowPage />} />
                <Route path="/workflow/sites/:siteId" element={<WorkflowPage />} />
                {/* The commercial chain is one screen: the supplier's
                    quotation, our order to them, the City's bill, the City's
                    order and our invoice are tabs on one job, as they are in
                    the purchase order generator. */}
                <Route path="/commercial" element={<CommercialPage />} />
                <Route path="/commercial/:id" element={<CommercialPage />} />
                {/* Where the five screens used to be. A register address opens
                    the one screen; a document address opens that document,
                    which is what an old bookmark or an emailed link means. */}
                <Route path="/quotations" element={<Navigate to="/commercial" replace />} />
                <Route path="/quotations/:id" element={<JobRoute />} />
                <Route path="/purchase-orders" element={<Navigate to="/commercial" replace />} />
                <Route path="/purchase-orders/:id" element={<DocumentPage kind="po" />} />
                <Route path="/boq" element={<Navigate to="/commercial" replace />} />
                <Route path="/boq/:id" element={<DocumentPage kind="boq" />} />
                <Route path="/customer-orders" element={<Navigate to="/commercial" replace />} />
                <Route path="/customer-orders/:id" element={<DocumentPage kind="customer" />} />
                <Route path="/invoices" element={<Navigate to="/commercial" replace />} />
                <Route path="/invoices/:id" element={<DocumentPage kind="invoice" />} />
                <Route path="/tracker" element={<TrackerPage />} />
                <Route path="/inspections" element={<InspectionsPage />} />
                <Route path="/inspections/:id" element={<InspectionsPage />} />
                <Route path="/inspections/:id/checks" element={<InspectionsPage />} />
                <Route path="/routing" element={<RoutingPage />} />
                <Route path="/routing/plans" element={<PlansPage />} />
                <Route path="/routing/plans/:id" element={<PlansPage />} />
                <Route path="/routing/overrides" element={<OverridesPage />} />
                <Route path="/reports" element={<Navigate to="/reports/visits" replace />} />
                <Route path="/reports/visits" element={<ReportPage />} />
                <Route path="/reports/dashboard" element={<VisitsDashboard />} />
                <Route path="/reports/redlist" element={<RedListReportPage />} />
                <Route path="/routing/report" element={<KeepSearch to="/reports/visits" />} />
                <Route path="/routing/dashboard" element={<KeepSearch to="/reports/dashboard" />} />
                <Route path="/routing/:planId/daysheets" element={<DaySheetsPage />} />
                <Route path="/schedule" element={<SchedulePage />} />
                <Route path="/crew" element={<CrewPage />} />
                <Route path="/crew/sites/:id" element={<CrewSitePage />} />
                <Route path="/crew/sites/:id/prep" element={<CrewSitePage />} />
                <Route path="/providers" element={<ProvidersPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/admin/spares" element={<SparesPage />} />
                <Route path="/admin/meeting-points" element={<MeetingPointsPage />} />
                <Route path="/admin/settings" element={<SettingsPage />} />
                <Route path="/admin/flange-tables" element={<FlangeTablesPage />} />
                <Route path="/admin/price-lists" element={<PriceListsPage />} />
                <Route path="/admin/price-lists/:id" element={<PriceListsPage />} />
                <Route path="/admin/labour-allowances" element={<LabourAllowancesPage />} />
                <Route path="/admin/audit" element={<AuditPage />} />
                <Route path="/admin/quotations-backload" element={<BackloadPage />} />
                <Route path="/admin" element={<Navigate to="/providers" replace />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Signed>
          </RequireAuth>
        }
      />
    </Routes>
  );
}
